import { IntlShape } from 'react-intl';

export interface SubscriptionReactivationInfoDefaults {
  intro?: string;
  text: string;
  textExtra?: string;
  buttonText: string;
}

// These three arrays hold message ids, not display text. SubscriptionItem formats
// each one and supplies a `name` argument, so an entry may use {name} — that
// replaced a hand-rolled `item.replace('%{pet?.name}', …)` where the placeholder
// was the literal source expression written out as a string.
//
// SKIP_SUBSCRIPTION is currently referenced only from a commented-out entry in
// SubscriptionLifecycle, so its copy does not reach the UI today.
export const SKIP_SUBSCRIPTION = [
  'SUBSCRIPTION.SKIP.ITEM.ENOUGH_FOOD',
  'SUBSCRIPTION.SKIP.ITEM.NO_SPACE',
  'SUBSCRIPTION.SKIP.ITEM.TRAVELLING',
  'SUBSCRIPTION.SKIP.ITEM.JUST_SKIP',
];

export const PAUSE_SUBSCRIPTION = [
  'SUBSCRIPTION.PAUSE.ITEM.BREAK',
  'SUBSCRIPTION.PAUSE.ITEM.NO_COST',
  'SUBSCRIPTION.PAUSE.ITEM.CHOOSE_WEEKS',
];

export const CANCEL_SUBSCRIPTION = [
  'SUBSCRIPTION.CANCEL.ITEM.NO_MORE_FRESH',
  'SUBSCRIPTION.CANCEL.ITEM.NO_MORE_JOY',
  'SUBSCRIPTION.CANCEL.ITEM.CANNOT_CHOOSE',
];

export enum RECIPES {
  CG = 'CG',
  GS = 'GS',
  RT = 'RT',
  WS = 'WS',
}

// `name` is the product/brand name and is deliberately not translated. `accentId`
// and `benefits` hold message ids rather than display text — Recipe formats them.
// The card title is authored as two halves (a shared lead plus a per-recipe accent)
// instead of being word-split at render time, because "last two words" only lands
// on the meat phrase by accident and breaks as soon as a language words it
// differently.
export const ALL_RECIPES = [
  {
    id: RECIPES.CG,
    name: 'Cowgenuss',
    accentId: 'RECIPE.CG.ACCENT',
    imageMain:
      'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_Ringeltanz_Frischfutter_mit_Rind_desktop_3_1.jpg?v=1790673527',
    imageMainMobile:
      'https://cdn.shopify.com/s/files/1/0835/9532/9839/files/Cowgenuss_Frischfutter_mit_mobile_1.webp?v=1785234887',
    imageHover:
      'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_ingredients_bg.jpg?v=1769612545',
    benefits: ['RECIPE.BENEFIT.PROTEIN_RICH', 'RECIPE.BENEFIT.MUSCLE'],
  },
  {
    id: RECIPES.GS,
    name: 'Gockelspeis',
    accentId: 'RECIPE.GS.ACCENT',
    imageMain:
      'https://cdn.shopify.com/s/files/1/0835/9532/9839/files/Gockelspeis_Frischfutter_mit_Huhn_desktop.webp?v=1785234887',
    imageMainMobile:
      'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/GS-mobile-2026_1.jpg?v=1787642172',
    imageHover:
      'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Gockelspeis_ingredients_bg.jpg?v=1769612545',
    benefits: ['RECIPE.BENEFIT.DIGESTIBLE', 'RECIPE.BENEFIT.ENERGY'],
  },
  {
    id: RECIPES.RT,
    name: 'Ringeltanz',
    accentId: 'RECIPE.RT.ACCENT',
    disabled: false,
    imageMain:
      'https://cdn.shopify.com/s/files/1/0835/9532/9839/files/Ringeltanz_Frischfutter_mit_Schwein__desktop.webp?v=1785234887',
    imageMainMobile:
      'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/schweinjpg.jpg?v=1787586469',
    imageHover:
      'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Ringeltanz_ingredients_bg.jpg?v=1769612543',
    benefits: ['RECIPE.BENEFIT.HEALTHY_FATS', 'RECIPE.BENEFIT.SHINY_COAT'],
  },
  {
    id: RECIPES.WS,
    name: 'Wildschmaus',
    accentId: 'RECIPE.WS.ACCENT',
    disabled: false,
    imageMain:
      'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Wildschmaus-frisch_gekochtes_Hundefutter_mit_Wild_Zutaten.webp?v=1786096174',
    imageMainMobile:
      'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Wildschmaus_-_frisch_gekochtes_Hundefutter_mit_Wild_Zutaten.webp?v=1786096174',
    imageHover:
      'https://cdn.shopify.com/s/files/1/0835/9532/9839/files/Wildschmaus_-_frisch_gekochtes_Hundefutter_mit_Wild_Zutaten_2.webp?v=1786105404',
    benefits: ['RECIPE.BENEFIT.PROTEIN_RICH', 'RECIPE.BENEFIT.DIGESTIBLE'],
    hasBadge: true,
  },
];

/**
 * Recipes that are temporarily hidden from the UI and force-deselected everywhere.
 *
 * WS ("Wildschmaus") is fully wired up across the dashboard, but its content/assets/Shopify
 * data are not finalised yet, so we hide it from the recipe cards + product-update flow and
 * make sure it can never stay selected (recalculation still runs, just without WS). To
 * re-enable a recipe once it's ready, remove it from this list — no other code changes needed.
 */
export const HIDDEN_RECIPES: RECIPES[] = [];

export const isHiddenRecipe = (id: string): boolean => HIDDEN_RECIPES.includes(id as RECIPES);

/** Recipe cards that should actually be rendered (source of truth minus hidden). */
//export const VISIBLE_RECIPES = ALL_RECIPES.filter((recipe) => !isHiddenRecipe(recipe.id));
export const VISIBLE_RECIPES = ALL_RECIPES;

// Both of these return already-formatted text, so SubscriptionReactivationInfo stays
// a plain renderer and does not need to know about message ids or their arguments.
// They take `intl` because they are called outside a component body — the same shape
// petcon uses for createPhoneValidation(intl).
export const getSubscriptionResume = (intl: IntlShape): SubscriptionReactivationInfoDefaults => ({
  text: intl.formatMessage({ id: 'SUBSCRIPTION.RESUME.TEXT' }),
  textExtra: intl.formatMessage({ id: 'SUBSCRIPTION.RESUME.TEXT_EXTRA' }),
  buttonText: intl.formatMessage({ id: 'SUBSCRIPTION.RESUME.BUTTON' }),
});

export const getSubscriptionReactivate = (
  intl: IntlShape,
  pet?: {
    name: string;
    gender?: string;
  },
): SubscriptionReactivationInfoDefaults => ({
  intro: intl.formatMessage({ id: 'SUBSCRIPTION.REACTIVATE.INTRO' }),
  // The possessive was `pet?.gender === 'm' ? 'sein' : 'ihr'`. It is an ICU select
  // inside the message now, so a language whose possessive agrees with the noun
  // rather than the owner (French: "sa nourriture" either way) can drop the
  // distinction instead of being forced to pick one of two German words.
  text: intl.formatMessage(
    { id: 'SUBSCRIPTION.REACTIVATE.TEXT' },
    {
      name: pet?.name || intl.formatMessage({ id: 'SUBSCRIPTION.REACTIVATE.PET_FALLBACK' }),
      gender: pet?.gender ?? '',
    },
  ),
  buttonText: intl.formatMessage({ id: 'SUBSCRIPTION.REACTIVATE.BUTTON' }),
});

export const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 3,
  className: 'mx-auto recipe-slider w-full',
  mobileFirst: true,
  responsive: [
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};
